// Pure, framework-agnostic filter / sort / search helpers for the Projects page.
// Extracted so the logic is unit-testable without rendering React components.

export type SortMode = "random" | "newest" | "oldest" | "alpha" | "reverse-alpha";
export type ViewMode = "classic" | "compact" | "tagcloud" | "timeline";

export interface ProjectTag {
  tag: { id: number | string; name: string };
}

export interface TeamMember {
  name: string;
  role?: string;
  title?: string;
  triad?: number;
}

export interface ExecSponsor {
  name: string;
  title?: string;
}

/** The three roles that make up a triad, in the order they are always presented. */
export const TRIAD_ROLES = ["Mentee", "Mentor", "Committee Member"] as const;

export type TriadRole = (typeof TRIAD_ROLES)[number];

export interface TriadSlot {
  role: TriadRole;
  member?: TeamMember;
}

export interface Triad {
  /** 1-based triad number as captured on the project. */
  number: number;
  slots: TriadSlot[];
}

/**
 * Normalise a free-text roster role to one of the three triad roles.
 *
 * The role is authored in spreadsheets and bulk imports, so the same role arrives under
 * several spellings - the current data set alone contains both "committee member" and
 * "committee". Exported so every surface that partitions a roster (the project card, the
 * print booklet) resolves roles identically instead of hand-rolling an equality check that
 * silently drops a spelling.
 */
export const triadRoleOf = (role?: string): TriadRole | undefined => {
  const normalized = (role ?? "").trim().toLowerCase();
  if (normalized === "mentee") return "Mentee";
  if (normalized === "mentor") return "Mentor";
  if (
    normalized === "committee" ||
    normalized === "committee member" ||
    normalized === "committee mentor" ||
    normalized === "triad committee member" ||
    normalized === "tcm"
  ) {
    return "Committee Member";
  }
  return undefined;
};

/**
 * Group a flat roster into triads.
 *
 * A triad is Mentee + Mentor + Committee Member. The executive sponsor is deliberately NOT
 * part of one: a sponsor backs the whole team, across every triad on the project, so
 * listing them alongside triad members misrepresents the program. Callers render sponsors
 * separately.
 *
 * A project has at least one triad and commonly two; three happens occasionally. Every
 * triad always reports all three slots, with `member` left undefined where nobody is
 * recorded, because a silently missing role reads as "this triad has no committee member"
 * rather than "nobody filled that column in".
 */
export const groupIntoTriads = (members: TeamMember[] = []): { triads: Triad[]; unassigned: TeamMember[] } => {
  const byNumber = new Map<number, Map<TriadRole, TeamMember>>();
  const unassigned: TeamMember[] = [];

  for (const member of members) {
    const role = triadRoleOf(member.role);
    if (!role) {
      unassigned.push(member);
      continue;
    }
    // A roster with no triad numbers at all is the common single-triad case.
    const number = Number.isInteger(member.triad) && (member.triad as number) > 0 ? (member.triad as number) : 1;
    let slots = byNumber.get(number);
    if (!slots) {
      slots = new Map();
      byNumber.set(number, slots);
    }
    // Two people in the same role and triad can't both hold the slot; the extra is surfaced
    // rather than dropped.
    if (slots.has(role)) unassigned.push(member);
    else slots.set(role, member);
  }

  const triads = [...byNumber.entries()]
    .sort(([a], [b]) => a - b)
    .map(([number, slots]) => ({
      number,
      slots: TRIAD_ROLES.map((role) => ({ role, member: slots.get(role) })),
    }));

  return { triads, unassigned };
};

export interface Project {
  id: number | string;
  title: string;
  description?: string;
  year: number | string;
  logo?: string;
  Chapter?: { location?: string } | null;
  tags?: ProjectTag[];
  team?: { team?: TeamMember[] } | null;
  exec_sponsor?: ExecSponsor[] | null;
  [key: string]: unknown;
}

export interface PipelineOptions {
  years?: Array<number | string>;
  tags?: string[];
  locations?: string[];
  search?: string;
  sort?: SortMode;
  seed?: number;
}

const norm = (value: unknown): string =>
  typeof value === "string" ? value.toLowerCase() : String(value ?? "").toLowerCase();

/**
 * Additive multi-select year filter. When no years are selected every project
 * passes through. Years are compared loosely so number/string mismatches match.
 */
export function filterByYear<T extends Project>(projects: T[], selectedYears: Array<number | string>): T[] {
  if (!selectedYears || selectedYears.length === 0) return projects;
  const wanted = new Set(selectedYears.map((y) => String(y)));
  return projects.filter((p) => wanted.has(String(p.year)));
}

/**
 * Tag filter — OR within the dimension. A project passes if it carries at least
 * one of the selected tags. Empty selection is a no-op.
 */
export function filterByTags<T extends Project>(projects: T[], selectedTags: string[]): T[] {
  if (!selectedTags || selectedTags.length === 0) return projects;
  const wanted = new Set(selectedTags.map(norm));
  return projects.filter((p) => (p.tags ?? []).some((t) => wanted.has(norm(t?.tag?.name))));
}

/**
 * Location/chapter filter — OR within the dimension. Empty selection is a no-op.
 */
export function filterByLocations<T extends Project>(projects: T[], selectedLocations: string[]): T[] {
  if (!selectedLocations || selectedLocations.length === 0) return projects;
  const wanted = new Set(selectedLocations.map(norm));
  return projects.filter((p) => wanted.has(norm(p.Chapter?.location)));
}

/**
 * Case-insensitive substring search across title, description, team member
 * names, exec sponsor names, and tag names. Empty/whitespace query is a no-op.
 */
export function searchProjects<T extends Project>(projects: T[], query: string): T[] {
  const q = norm(query).trim();
  if (!q) return projects;
  return projects.filter((p) => {
    const haystacks: string[] = [
      norm(p.title),
      norm(p.description),
      ...(p.team?.team ?? []).map((m) => norm(m?.name)),
      ...(p.exec_sponsor ?? []).map((s) => norm(s?.name)),
      ...(p.tags ?? []).map((t) => norm(t?.tag?.name)),
    ];
    return haystacks.some((h) => h.includes(q));
  });
}

// Deterministic PRNG (mulberry32) so "Random" order stays stable across
// re-renders/filtering within a single page load when seeded.
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function fisherYates<T>(items: T[], rand: () => number): T[] {
  const out = items.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

const yearNum = (p: Project): number => {
  const n = Number(p.year);
  return Number.isFinite(n) ? n : 0;
};

const byTitle = (a: Project, b: Project): number =>
  norm(a.title).localeCompare(norm(b.title));

/**
 * Sort projects by mode. Random uses a seeded Fisher-Yates shuffle (stable per
 * page load when a seed is supplied). Newest/Oldest sort by year with an
 * alphabetical-by-title tiebreak within the same year. Alpha/Reverse-alpha sort
 * purely by title (A→Z / Z→A). Does not mutate input.
 */
export function sortProjects<T extends Project>(projects: T[], mode: SortMode, seed?: number): T[] {
  const list = projects.slice();
  switch (mode) {
    case "newest":
      return list.sort((a, b) => yearNum(b) - yearNum(a) || byTitle(a, b));
    case "oldest":
      return list.sort((a, b) => yearNum(a) - yearNum(b) || byTitle(a, b));
    case "alpha":
      return list.sort(byTitle);
    case "reverse-alpha":
      return list.sort((a, b) => byTitle(b, a));
    case "random":
    default:
      return fisherYates(list, typeof seed === "number" ? mulberry32(seed) : Math.random);
  }
}

/**
 * Chain all filters (year → tags → locations → search) then apply the sort.
 */
export function applyPipeline<T extends Project>(projects: T[], options: PipelineOptions = {}): T[] {
  const { years = [], tags = [], locations = [], search = "", sort = "random", seed } = options;
  let result = filterByYear(projects, years);
  result = filterByTags(result, tags);
  result = filterByLocations(result, locations);
  result = searchProjects(result, search);
  result = sortProjects(result, sort, seed);
  return result;
}
